import { createBaseApi } from '../baseApi';
import { API_ENDPOINTS } from '../../../utils/config';

/**
 * RTK Query API for the SUSU Closet Shared Closet feature.
 * Handles browsing community-shared items, toggling share status,
 * and retrieving sharing statistics.
 */
const sharedClosetApi = createBaseApi('sharedClosetApi');

// Enhanced API with shared closet endpoints
export const enhancedSharedClosetApi = sharedClosetApi.injectEndpoints({
  endpoints: (builder) => ({
    communityPage: builder.query({
      query: (params) => ({ url: API_ENDPOINTS.SHARED_CLOSET.BROWSE, params }),
      providesTags: ['SharedCloset'],
      transformResponse: response => ({ items: response.data || response.results || response.items || [], pagination: response.pagination })
    }),

    /**
     * Browse all shared closet items from the community.
     * Supports pagination via _start/_limit and sorting via _sort.
     */
    browseSharedCloset: builder.query({
      query: (args = {}) => {
        const _start = args._start !== undefined ? args._start : (args.page ? (args.page - 1) * (args.pageSize || 20) : 0);
        const _limit = args._limit !== undefined ? args._limit : (args.pageSize || 20);
        const _sort = args._sort || (args.sort === 'recent' ? 'created_at:DESC' : args.sort) || 'created_at:DESC';
        return {
          url: API_ENDPOINTS.SHARED_CLOSET.BROWSE,
          method: 'GET',
          params: { _start, _limit, _sort, seed: args.seed }
        };
      },
      providesTags: ['SharedCloset'],
      transformResponse: (response) => response.results || response.items || response || []
    }),

    /**
     * Get a specific user's shared closet items.
     * @param {string} userId - The user whose shared items to fetch
     */
    getUserSharedCloset: builder.query({
      query: ({ userId, _start = 0, _limit = 20, page, pageSize }) => {
        const start = _start !== undefined ? _start : (page ? (page - 1) * (pageSize || 20) : 0);
        const limit = _limit !== undefined ? _limit : (pageSize || 20);
        return {
          url: API_ENDPOINTS.SHARED_CLOSET.USER_CLOSET(userId),
          method: 'GET',
          params: { _start: start, _limit: limit }
        };
      },
      providesTags: ['SharedCloset'],
      transformResponse: (response) => response.results || response.items || response || []
    }),

    /**
     * Toggle the share status of a closet item.
     * Shares or un-shares the item from the community closet.
     * @param {string|number|{ entityType?: string, entityId?: number, clothesId?: number }} arg - The closet clothes item ID to toggle
     */
    toggleShareItem: builder.mutation({
      query: (arg) => {
        const clothesId = typeof arg === 'object' && arg !== null ? (arg.clothesId || arg.entityId || arg.id) : arg;
        return {
          url: API_ENDPOINTS.SHARED_CLOSET.TOGGLE(clothesId),
          method: 'PUT'
        };
      },
      invalidatesTags: ['SharedCloset', 'ClosetClothes']
    }),

    /**
     * Get the current user's sharing statistics.
     * Returns totals for items shared, try-ons received, credits earned, etc.
     */
    getMySharedStats: builder.query({
      query: () => ({
        url: API_ENDPOINTS.SHARED_CLOSET.MY_STATS,
        method: 'GET'
      }),
      providesTags: ['SharedCloset', 'Earnings']
    }),

    /**
     * Get the credit cost to try on a specific shared item.
     * @param {string|number|{ entityType?: string, entityId?: number, clothesId?: number }} arg - The shared closet clothes item ID
     * @returns {{ cost: number, isShared: boolean, sharerName: string, sharerId: string }}
     */
    getCreditCost: builder.query({
      query: (arg) => {
        const clothesId = typeof arg === 'object' && arg !== null ? (arg.clothesId || arg.entityId || arg.id) : arg;
        return {
          url: API_ENDPOINTS.SHARED_CLOSET.CREDIT_COST(clothesId),
          method: 'GET'
        };
      },
      transformResponse: (response) => ({
        cost: response.cost || 1,
        isShared: response.isShared,
        sharerName: response.sharerName,
        sharerId: response.sharerId,
        licensingPrice: response.licensingPrice || 0,
        creditCost: response.cost || response.creditCost || 1,
        totalCost: response.totalCost || 0,
        isOwner: response.isOwner || false,
        creatorUsername: response.sharerName || response.creatorUsername || 'Unknown',
        creatorId: response.sharerId || response.creatorId,
        isPublic: response.isPublic,
        licensingEnabled: response.licensingEnabled
      })
    })
  })
});

export const {
  useCommunityPageQuery,
  useBrowseSharedClosetQuery,
  useGetUserSharedClosetQuery,
  useToggleShareItemMutation,
  useGetMySharedStatsQuery,
  useGetCreditCostQuery
} = enhancedSharedClosetApi;

export default enhancedSharedClosetApi;
